<template>
	<section
		class="flex flex-col items-start self-stretch justify-center gap-4 p-6 bg-white border border-gray-200 rounded-lg"
	>
		<div v-if="loading" class="self-stretch py-10 text-center">
			<Spinner type="primary">
				{{ $t('application-history.loading') }}
			</Spinner>
		</div>

		<div v-else-if="error" class="self-stretch py-10 text-center">
			<IconAlertCircle
				:size="32"
				class="block mx-auto mb-3 text-red-500"
			/>
			<p class="mb-4 text-sm text-gray-600">
				{{ $t('application-history.error') }}
			</p>
			<Button type="secondary" size="sm" @click="fetchHistory">
				{{ $t('general.retry') }}
			</Button>
		</div>

		<div
			v-else-if="!groups.length"
			class="flex flex-col items-center self-stretch gap-4 py-12 text-center"
		>
			<div
				class="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full shrink-0"
			>
				<IconClock :size="24" class="text-grey-700 shrink-0" />
			</div>
			<div class="flex flex-col gap-1">
				<p class="text-base font-medium leading-6 text-gray-950">
					{{ $t('application-history.empty-title') }}
				</p>
				<p class="text-sm leading-5 text-gray-700">
					{{ $t('application-history.empty-description') }}
				</p>
			</div>
		</div>

		<div v-else class="self-stretch">
			<div v-for="(group, groupIndex) in groups" :key="group.key">
				<div
					class="flex items-center self-stretch min-w-0 gap-4 pb-4"
					:class="groupIndex > 0 ? 'pt-6' : ''"
				>
					<p
						class="m-0 text-xs font-medium leading-4 text-gray-600 uppercase shrink-0"
					>
						{{ group.label }}
					</p>
					<div class="flex-1 h-px bg-gray-200" />
				</div>

				<ol class="mb-0 space-y-0">
					<li
						v-for="(event, eventIndex) in group.events"
						:key="event.key"
						class="flex items-stretch"
					>
						<div
							class="flex w-[30px] shrink-0 flex-col items-center"
						>
							<span
								class="flex h-[30px] w-[30px] items-center justify-center overflow-hidden rounded-full text-sm"
								:class="
									event.usesActorAvatar
										? ''
										: `border ${timelineMarkerClass(event)}`
								"
							>
								<img
									v-if="
										event.usesActorAvatar &&
											event.actorAvatarUrl
									"
									:src="event.actorAvatarUrl"
									:alt="event.actorName"
									class="object-cover w-full h-full"
								/>
								<IconUserProfile
									v-else-if="event.usesActorAvatar"
									:size="30"
									class="text-grey-300 shrink-0"
								/>
								<Component
									:is="timelineMarkerIcon(event)"
									v-else
									:size="14"
									class="shrink-0"
								/>
							</span>
							<span
								v-if="!isLastEvent(group.events, eventIndex)"
								class="min-h-[22px] w-px flex-1 bg-gray-300"
							/>
						</div>

						<div
							class="flex-1 min-w-0 pl-4"
							:class="
								isLastEvent(group.events, eventIndex)
									? ''
									: 'pb-8'
							"
						>
							<div
								class="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-12"
							>
								<div class="flex-1 min-w-0">
									<p
										class="flex min-h-[30px] items-center mb-0 text-sm leading-5 break-words text-gray-950"
									>
										{{ event.title }}
									</p>
									<CollapsibleRichTextNote
										v-if="
											isNoteEvent(event) &&
												event.noteContent
										"
										class="mt-1"
										:content="event.noteContent"
										:collapsed-height="40"
										:more-label="
											$t('application-history.show-more')
										"
										:less-label="
											$t('application-history.show-less')
										"
									/>
									<p
										v-else-if="event.details"
										class="mt-1 text-sm leading-5 text-gray-600 break-words"
									>
										{{ detailsText(event) }}
									</p>
									<button
										v-if="
											event.canExpand &&
												!isNoteEvent(event)
										"
										type="button"
										class="mt-2 text-sm font-medium text-blue-700 hover:text-blue-800"
										@click="toggleExpanded(event.key)"
									>
										{{
											isExpanded(event.key)
												? $t(
														'application-history.show-less'
												  )
												: $t(
														'application-history.show-more'
												  )
										}}
									</button>
								</div>

								<p
									class="m-0 text-xs leading-5 text-gray-600 shrink-0 whitespace-nowrap sm:flex sm:min-h-[30px] sm:items-center sm:gap-1"
								>
									<span class="font-medium text-gray-700">
										{{ event.actorName }}
									</span>
									<span> • {{ event.timeLabel }}</span>
								</p>
							</div>
						</div>
					</li>
				</ol>
			</div>
		</div>
	</section>
</template>

<script>
import Button from '~/components/ui/common/Button'
import {
	IconAlertCircle,
	IconClock,
	IconUserProfile,
	IconArrowRight,
	IconPaperPlane,
	IconTimesCircle,
	IconCalendarEmpty,
	IconCheckCircle,
	IconDialogue,
	IconUser,
	IconRepeat,
	IconBell,
	IconArrowLeft,
	IconInfoCircle
} from '~/components/Icons'
import Spinner from '~/components/ui/common/Spinner'
import CollapsibleRichTextNote from '~/components/ui/notes/CollapsibleRichTextNote'
import { normalizeApplicationHistoryEvents } from '~/utils/applicationHistory'

export default {
	name: 'ApplicantApplicationHistory',
	components: {
		Button,
		IconAlertCircle,
		IconClock,
		IconUserProfile,
		Spinner,
		CollapsibleRichTextNote
	},
	props: {
		application: {
			type: Object,
			required: true
		},
		applicantName: {
			type: String,
			required: false,
			default: ''
		},
		applicantAvatarUrl: {
			type: String,
			required: false,
			default: null
		},
		refreshKey: {
			type: Number,
			required: false,
			default: 0
		}
	},
	data() {
		return {
			events: [],
			loading: false,
			error: false,
			expandedRows: {},
			requestSequence: 0
		}
	},
	computed: {
		applicationId() {
			return this.application?.id || null
		},
		locale() {
			return this.$i18n?.locale || this.$store?.state?.locale || 'en'
		},
		groups() {
			return normalizeApplicationHistoryEvents(this.events, {
				t: (key, params) => this.$t(key, params),
				locale: this.locale,
				formatDate: (date, format) => this.formatDate(date, format),
				applicantName: this.applicantName,
				applicantAvatarUrl: this.applicantAvatarUrl
			})
		}
	},
	watch: {
		applicationId: {
			handler(newId, oldId) {
				if (newId && newId !== oldId) {
					this.fetchHistory()
				}
			}
		},
		refreshKey(newKey, oldKey) {
			if (newKey !== oldKey) {
				this.fetchHistory()
			}
		}
	},
	mounted() {
		this.fetchHistory()
	},
	methods: {
		async fetchHistory() {
			const applicationId = this.applicationId

			if (!applicationId) {
				return
			}

			const requestId = ++this.requestSequence
			this.loading = true
			this.error = false

			try {
				const response = await this.$axios.$get(
					`/application/${applicationId}/list`
				)

				if (!this.isCurrentRequest(requestId, applicationId)) {
					return
				}

				this.events = Array.isArray(response) ? response : []
			} catch (error) {
				if (!this.isCurrentRequest(requestId, applicationId)) {
					return
				}

				this.error = true
				this.events = []
			} finally {
				if (this.isCurrentRequest(requestId, applicationId)) {
					this.loading = false
				}
			}
		},
		isCurrentRequest(requestId, applicationId) {
			return (
				requestId === this.requestSequence &&
				applicationId === this.applicationId
			)
		},
		formatDate(date, format) {
			if (typeof this.$formatDate === 'function') {
				return this.$formatDate(date, format)
			}

			return null
		},
		timelineMarkerIcon(event) {
			const icons = {
				'arrow-right': IconArrowRight,
				'paper-plane': IconPaperPlane,
				declined: IconTimesCircle,
				calendar: IconCalendarEmpty,
				hired: IconCheckCircle,
				comment: IconDialogue,
				clock: IconClock,
				hourglass: IconClock,
				'calendar-alt': IconCalendarEmpty,
				'check-circle': IconCheckCircle,
				'times-circle': IconTimesCircle,
				user: IconUser,
				sync: IconRepeat,
				'bell-plus': IconBell,
				reply: IconArrowLeft
			}

			return icons[event.markerIcon || event.icon?.[1]] || IconInfoCircle
		},
		timelineMarkerClass(event) {
			const classes = {
				green: 'border-[#CBE7D7] bg-[#E6F4EC] text-[#2B503C]',
				red: 'border-red-200 bg-red-100 text-[#99241B]',
				blue: 'border-[#CEE1E9] bg-[#E6EFF4] text-[#2C5060]',
				gray: 'border-gray-200 bg-white text-gray-600'
			}

			return classes[event.tone] || classes.gray
		},
		isLastEvent(events, index) {
			return index === events.length - 1
		},
		isExpanded(key) {
			return Boolean(this.expandedRows[key])
		},
		toggleExpanded(key) {
			this.$set(this.expandedRows, key, !this.expandedRows[key])
		},
		isNoteEvent(event) {
			return event.type === 'comment_added'
		},
		detailsText(event) {
			return this.isExpanded(event.key)
				? event.details
				: event.previewDetails
		}
	}
}
</script>
